/**
 * Helper: Format
 * -----------------------------------------------------------------------------
 * Formats values and objects.
 * - Add in alphabetical order.
 *
 */
/* eslint-disable no-constant-binary-expression */
import { formatMoney } from '@shopify/theme-currency'

import { convertToHandle } from '~/helpers/convert'
import { applyConversionRate } from '~/helpers/general'
import { decodeApiId, transformEdges } from '~/helpers/graphql'

/**
 * Formats response from product.
 * @param {Object} response - GraphQL response.
 * @returns {Object}
 */
export function formatProduct(response) {
  const product = response.product
  const mediaData = formatMedia(product)

  const formattedProduct = {
    available: product.available,
    compareAtPrice: formatPrice(product.compareAtPriceRange),
    description: product.descriptionHtml,
    handle: product.handle,
    id: decodeApiId(product.id),
    images: formatImages(product),
    videos: formatVideos(product),
    videoIndexes: mediaData.videoIndexes,
    media: mediaData.gallery,
    options: formatOptions(product),
    price: formatPrice(product.priceRange),
    tags: product.tags,
    title: product.title,
    type: product.productType,
    url: `//${location.host}/products/${product.handle}`,
    modelSizeCaption: product.modelSizeCaption ? product.modelSizeCaption : null,
    preorderShippingDate: product.preorderShippingDate ? product.preorderShippingDate : null,
    machineWash: product.machineWash ? product.machineWash : null,
    fabric: product.fabric ? product.fabric : null,
    imported: product.imported ? product.imported : null,
    careGuide: product.careGuide ? product.careGuide : null,
    sizeFit: product.sizeFit ? product.sizeFit : null,
    featuresDetails: product.featuresDetails ? product.featuresDetails : null,
    styledWith: product.styledWith && product.styledWith.product
      ? {
        id: product.styledWith.id,
        value: product.styledWith.value,
        product: formatProduct({ product: product.styledWith.product }),
      }
      : null,
    sizeGuideId: product.sizeGuide ? product.sizeGuide : null,
  }

  /**
   * Add additional values which rely on formatted response.
   */
  formattedProduct.featuredImage = formattedProduct.images[0]
  formattedProduct.giftCard = formattedProduct.type === 'Gift Cards'
  formattedProduct.variants = formatVariants(product, formattedProduct.giftCard)

  formattedProduct.hasOnlyDefaultVariant = formattedProduct.variants.every((variant) => {
    return variant.title.trim().toLowerCase() === 'default title'
  })

  return formattedProduct
}

/**
 * Formats price.
 * @param {Object} price - Price response.
 * @param {Boolean} convert - Apply shopify conversion rate.
 * @returns {Object}
 */
function formatPrice(price, convert = false) {
  let amount = (Number(price?.minVariantPrice.amount) * 100) || false
  let max = (Number(price?.maxVariantPrice.amount) * 100) || false
  let min = (Number(price?.minVariantPrice.amount) * 100) || false

  if (convert) {
    amount = applyConversionRate(amount)
    max = applyConversionRate(max)
    min = applyConversionRate(min)
  }

  return {
    amount,
    currencyCode: price?.minVariantPrice.currencyCode,
    max,
    min,
    varies: price?.maxVariantPrice.amount !== price?.minVariantPrice.amount,
  }
}

/**
 * Formats all media (images and video). Used for PDP gallery.
 * @param {Object} response - GraphQL response.
 * @returns {Array}
 */
function formatMedia(product) {
  if (!product.mediaComponents) {
    return {}
  }

  const mediaArray = transformEdges(product.mediaComponents)
  const mediaVideoIndexes = []
  let imageCurrentIndex = 0

  const formattedMedia = mediaArray.map((media, index) => {
    if (media.mediaType === 'VIDEO') {
      mediaVideoIndexes.push(index)
      return {
        alt: media.alt,
        sources: media.sources,
        poster: media.previewImage.url,
      }
    }

    if (media.mediaType === 'IMAGE') {
      const linkedVariant = product.variants?.length
        ? transformEdges(product.variants).find((variant) => {
          return variant.image.id === media.id
        })
        : false
      const imageObject = {
        alt: media.previewImage.altText,
        height: media.previewImage.height,
        id: decodeApiId(media.id),
        src: media.previewImage.url,
        variant: decodeApiId(linkedVariant?.id) || false,
        width: media.previewImage.width,
        index: imageCurrentIndex,
      }
      imageCurrentIndex += 1

      return imageObject
    }

    return {}
  })

  return { gallery: formattedMedia, videoIndexes: mediaVideoIndexes }
}

/**
 * Formats images.
 * @param {Object} response - GraphQL response.
 * @returns {Array}
 */
function formatImages(product, images) {
  const imagesArray = images ? images : transformEdges(product.images)

  const formattedImages = imagesArray.map((image) => {
    if (!image.url) {
      return {}
    }

    const linkedVariant = product.variants?.length
      ? transformEdges(product.variants).find((variant) => {
        return variant.image.id === image.id
      })
      : false

    return {
      alt: image.altText,
      height: image.height,
      id: decodeApiId(image.id),
      src: image.url,
      variant: decodeApiId(linkedVariant?.id) || false,
      width: image.width,
    }
  })

  if (images) {
    return formattedImages[0]
  }

  return formattedImages
}

/**
 * Formats videos.
 * @param {Object} response - GraphQL response.
 * @returns {Array}
 */
function formatVideos(product) {
  if (!product.media) {
    return []
  }

  const videos = transformEdges(product.media).filter((media) => media.mediaType === 'VIDEO')
  const formattedVideos = videos.map((video) => {
    return {
      alt: video.alt,
      sources: video.sources,
      poster: video.previewImage.originalSrc,
    }
  })

  return formattedVideos
}

/**
 * Formats options.
 * @param {Object} response - GraphQL response.
 * @returns {Array}
 */
function formatOptions(product) {
  return product.options.map((option, index) => {
    return {
      index,
      name: option.name,
      values: option.values,
    }
  })
}

/**
 * Formats variants.
 * @param {Object} response - GraphQL response.
 * @param {Boolean} isGiftCard - Product type is gift card.
 * @returns {Array}
 */
function formatVariants(product, isGiftCard) {
  return transformEdges(product.variants).map((variant) => {
    const formattedVariant = {
      available: variant.available,
      compareAtPrice: {
        amount: (Number(variant.compareAtPrice?.amount) * 100) || false,
        currencyCode: variant.compareAtPrice?.currencyCode || false,
      },
      id: decodeApiId(variant.id),
      image: decodeApiId(variant.image?.id) || false,
      inventory: variant.quantityAvailable,
      price: {
        amount: Number(variant.price.amount) * 100,
        currencyCode: variant.price.currencyCode,
      },
      sku: variant.sku,
      selectedOptions: variant.selectedOptions.map((option) => {
        return {
          name: option.name,
          value: option.value,
        }
      }),
    }

    /**
     * Want for prices to be formatted before determining title.
     * - Gift cards show the price as their title for multi-currency purposes.
     */
    formattedVariant.title = isGiftCard
      ? formatMoney(formattedVariant.price.amount, cnvs.store.moneyFormat)
      : variant.title

    return formattedVariant
  })
}

/**
 * Formats response from collection.
 * @param {Object} response - GraphQL response.
 * @returns {Object}
 */
export function formatCollection(response) {
  const collection = response.collection

  const formattedCollection = {
    description: collection.descriptionHtml,
    handle: collection.handle,
    id: decodeApiId(collection.id),
    pageInfo: {
      ...collection.products?.pageInfo,
      currentPage: 1,
    },
    title: collection.title,
    url: `//${location.host}${cnvs.routes.normalisedRoot}collections/${collection.handle}`,
  }

  /**
   * Format products.
   */
  formattedCollection.products = transformEdges(collection.products).map((productResponse) => {
    const product = productResponse

    return {
      available: product.available,
      compareAtPrice: formatPrice(product.compareAtPriceRange),
      featuredImage: formatImages(product)[0],
      handle: product.handle,
      images: formatImages(product).slice(0, 2),
      videos: formatVideos(product),
      media: product.media,
      price: formatPrice(product.priceRange),
      tags: product.tags,
      title: product.title,
      type: product.type,
      url: `//${location.host}${cnvs.routes.normalisedRoot}collections/${collection.handle}/products/${product.handle}`,
    }
  })

  return formattedCollection
}

/**
 * Formats response from menu.
 * @param {String} [prefix] - Prefix to prepend to handle to make it unique.
 * @param {String} [previousHandle] - Previous tier's handle.
 * @param {Object} response - GraphQL response or nested items array.
 * @param {Number} [tier] - Current tier number.
 * @returns {Array}
 */
export function formatMenu({
  prefix = 'mobile',
  previousHandle,
  response,
  tier = 1,
} = {}) {
  const menu = response?.menu?.items.length ? response.menu.items : response

  return menu.map((object, index) => {
    const handle = previousHandle
      ? `${prefix}-${previousHandle}-${convertToHandle(object.title)}-${tier}-${index}`
      : `${prefix}-${convertToHandle(object.title)}-${tier}-${index}`

    const links = object.items?.length
      ? formatMenu({
        prefix,
        previousHandle: handle,
        response: object.items,
        tier: tier + 1,
      })
      : []

    /**
     * Storefront API URL always uses .myshopify.com domain.
     */
    const urlPath =
      object.url.split(cnvs.store.localisedPermanentDomain)[1] ||
      object.url.split(cnvs.store.permanentDomain)[1]

    return {
      handle,
      links,
      tier,
      title: object.title,
      url: `//${location.host}${cnvs.routes.normalisedRoot}${urlPath}`,
    }
  })
}

/**
 * Format Shopify image URL.
 * @param {String} src - Image URL, can include existing parameters.
 * @param {Object|String} options - Image options object, or image size string.
 * @param {String} options.crop - Crop image positioning when height and width
 * are set.
 * @param {Number} options.height - Height of image in pixels.
 * @param {String} options.pad_color - Background colour to pad image when image
 * cannot be cropped.
 * @param {Number} options.width - Width of image in pixels.
 * @returns {String}
 */
export function formatImageUrl(src, options) {
  if (!src.includes('cdn.shopify.com')) {
    return src
  }

  const filetype = src.split('.').reverse()[0]?.split('?')[0]
  let version = src.match(/(?!\?|&)(?<version>v=\d+)/gi)

  if (!filetype || !version) {
    return src
  }

  version = version[0]
  const parameters = []

  /**
   * Clean up image URL.
   * - Remove URL parameters.
   * - Standardise protocol.
   * - Remove progressive filetype prefix.
   * - Remove filetype.
   * - Remove sizing, crop, and scale.
   */
  let url = src
    .split('?')[0]
    .split('//')[1]
    .replace('.progressive.', '.')
    .replace(`.${filetype}`, '.')
    .replace(/(?<size>_(?:\d+)?x(?:\d+)?)?(?<crop>_crop_(?:top|center|bottom|left|right))?(?<scale>@(?:2|3)x)?\./g, '.')

  url = `//${url}${filetype}?${version}`

  /**
   * If no options provided then return URL.
   */
  if (!options) {
    return url
  }

  /**
   * Get parameters from options object.
   */
  if (typeof options === 'object') {
    Object.entries(options).forEach(([key, value]) => {
      if (!value || key === 'format') {
        return
      }

      let formattedValue = value

      if (key === 'pad_color') {
        formattedValue = value.replace('#', '')
      }

      parameters.push(`${key}=${formattedValue}`)
    })
  }

  /**
   * Get width and height parameters from options string.
   */
  if (typeof options === 'string') {
    const sizes = options.split('x')

    if (sizes[0]) {
      parameters.push(`width=${sizes[0]}`)
    }

    if (sizes[1]) {
      parameters.push(`height=${sizes[1]}`)
    }

    if (sizes[0] && sizes[1]) {
      parameters.push('crop=center')
    }
  }

  return parameters.length
    ? `${url}&${parameters.join('&')}`
    : url
}

/* eslint-disable no-undef */

/**
 * Formats boost options
 * @param {Object} product
 * @returns {Object}
 */
function formatBoostOptions(product) {
  return product.options_with_values.map((option, index) => {
    return {
      index,
      name: option.name,
      values: option.values.map((value) => value.title),
    }
  })
}

/**
 * Formats boost images
 * @param {Object} product
 * @returns {Object}
 */
function formatBoostImages(product) {
  return product.images_info.map((image) => {
    const src = image.src.split('.jpg').join('_1000x.jpg')

    return {
      alt: image.alt,
      height: image.height,
      id: image.id,
      src,
      width: image.width,
    }
  })
}

/**
 * Format boost variants
 * @param {Object} product
 * @param {Boolean} isGiftCard
 * @returns {Array}
 */
function formatBoostVariants(product, isGiftCard) {
  return product.variants.map((variant) => {
    const image = product.images_info.find((imageInfo) => {
      return imageInfo.src === variant.image
    })

    const formattedVariant = {
      available: variant.available,
      id: variant.id,
      image: image ? image.id : null,
      inventory: variant.inventory_quantity,
      price: {
        amount: Number(variant[
          `price_${window.Shopify.currency.active.toLowerCase()}`
        ] || variant.price) * 100,
      },
      selectedOptions: variant.merged_options.map((option) => {
        const data = option.split(':')
        return {
          name: data[0],
          value: data[1],
        }
      }),
    }

    /**
     * Want for prices to be formatted before determining title.
     * - Gift cards show the price as their title for multi-currency purposes.
     */
    formattedVariant.title = isGiftCard
      ? formatMoney(formattedVariant.price.amount, cnvs.store.moneyFormat)
      : variant.title

    return formattedVariant
  })
}

/**
 * Formats product from Boost API call.
 * @param {Object} product
 * @returns {Object}
 */
export function formatBoostProduct(product) {
  const url = cnvs.routes.root === '/'
    ? `//${location.host}/products/${product.handle}`
    : `//${location.host}${cnvs.routes.root}/products/${product.handle}`

  const localePricingData = product.prices?.find(
    (locale) => locale.currency === window.Shopify.currency.active,
  )

  const formattedProduct = {
    available: product.available,
    compareAtPrice: formatPrice({
      maxVariantPrice: {
        amount: localePricingData?.compare_at_price_max || product.compare_at_price_max,
      },
      minVariantPrice: {
        amount: localePricingData?.compare_at_price_min || product.compare_at_price_min,
      },
    }),
    description: product.body_html,
    handle: product.handle,
    id: product.id,
    images: formatBoostImages(product),
    options: formatBoostOptions(product),
    price: formatPrice({
      maxVariantPrice: {
        amount: localePricingData?.price_max || product.price_max,
      },
      minVariantPrice: {
        amount: localePricingData?.price_min || product.price_min,
      },
    }),
    tags: product.tags,
    title: product.title,
    type: product.product_type,
    url,
  }

  /**
   * Add additional values which rely on formatted response.
   */
  formattedProduct.featuredImage = formattedProduct.images[0]
  formattedProduct.giftCard = formattedProduct.type === 'Gift Cards'
  formattedProduct.variants = formatBoostVariants(product, formattedProduct.giftCard)

  formattedProduct.hasOnlyDefaultVariant = formattedProduct.variants.every((variant) => {
    return variant.title.trim().toLowerCase() === 'default title'
  })

  return formattedProduct
}
